// Shared session transformation utilities
import { API_BASE_URL } from "./config";
import type { BackendMessage } from "./api-client";
import type { UIMessage } from "ai";
import { parseToolResult } from "./utils";

export interface BaseSession {
  id: string;
  title: string;
  created_at: string;
  user_id: string;
}

export interface ChatSession extends BaseSession {
  visibility: "private";
  createdAt: Date;
  userId: string;
}

export interface StrategySession extends BaseSession {
  description: string;
  status: "active" | "draft" | "archived";
  performance?: number;
  tags?: string[];
}

// Transform raw session to chat format
export const transformToChat = (session: any): ChatSession => ({
  id: session.session_id,
  title: session.title || "New Chat",
  created_at: session.created_at,
  user_id: session.user_id,
  visibility: "private" as const,
  userId: session.user_id,
  createdAt: new Date(session.created_at),
});

// Transform raw session to strategy format
export const transformToStrategy = (session: any): StrategySession => ({
  id: session.session_id,
  title: session.title || "Untitled Strategy",
  description: session.description || "No description available",
  created_at: session.created_at,
  user_id: session.user_id,
  status: session.status || "draft",
  performance: session.performance,
  tags: session.tags || [],
});

// Transform backend messages to UIMessage format for useChat
export function convertToUIMessages(messages: BackendMessage[]): Array<UIMessage> {
  const result: UIMessage[] = [];

  const mergeAssistantPart = (target: any, part: any) => {
    if (part?.type === "reasoning") {
      const nextReasoning = String(part.reasoning ?? "").trim();
      if (!nextReasoning) return;

      const existingIndex = target.parts.findIndex(
        (candidate: any) => candidate?.type === "reasoning",
      );

      if (existingIndex === -1) {
        target.parts.push(part);
        return;
      }

      const existing = target.parts[existingIndex];
      const existingReasoning = String(existing.reasoning ?? "").trim();
      if (!existingReasoning.includes(nextReasoning)) {
        target.parts[existingIndex] = {
          ...existing,
          reasoning: existingReasoning
            ? `${existingReasoning}\n\n${nextReasoning}`
            : nextReasoning,
          details: [
            ...(Array.isArray(existing.details) ? existing.details : []),
            ...(Array.isArray(part.details) ? part.details : []),
          ],
        };
      }
      return;
    }

    if (part?.type === "tool-invocation") {
      const nextInvocation = part.toolInvocation;
      const existingIndex = target.parts.findIndex(
        (candidate: any) =>
          candidate?.type === "tool-invocation" &&
          candidate?.toolInvocation?.toolCallId === nextInvocation?.toolCallId,
      );

      if (existingIndex === -1) {
        target.parts.push(part);
        return;
      }

      const existing = target.parts[existingIndex];
      if (nextInvocation?.state === "result") {
        target.parts[existingIndex] = part;
        return;
      }

      target.parts[existingIndex] = {
        ...existing,
        toolInvocation: {
          ...existing.toolInvocation,
          ...nextInvocation,
        },
      };
      return;
    }

    target.parts.push(part);
  };

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const parts = [];
    const attachments: Array<{
      url: string;
      name: string;
      contentType: "image/png" | "image/jpg" | "image/jpeg";
    }> = [];

    if (message.reasoning) {
      parts.push({
        type: "reasoning",
        reasoning: message.reasoning,
        details: [],
      });
    }

    const assistantText =
      typeof message.content === "string" &&
      message.content.trim() &&
      message.content !== "[Tool call]"
        ? message.content
        : "";

    if (message.role === "user") {
      if (Array.isArray(message.content)) {
        message.content.forEach((contentItem) => {
          // Hidden, system-injected context blocks (the engineered skill
          // instruction, requested-docs context, etc.) are tagged by the
          // backend and must NEVER render in the user bubble. Live streaming
          // builds the bubble from the user's typed text only, so this only
          // bites on reload-from-DB — which was leaking the whole skill prompt.
          const hiddenTag =
            (contentItem as { tag?: string }).tag === "skill_instruction_metadata" ||
            (contentItem as { tag?: string }).tag === "data_requested_metadata";
          if (hiddenTag) {
            return;
          }
          if (contentItem.type === "text" && contentItem.text) {
            parts.push({ type: "text", text: contentItem.text });
          } else if (
            contentItem.type === "image_url" &&
            contentItem.image_url?.url
          ) {
            attachments.push({
              url: contentItem.image_url.url,
              name: "image",
              contentType: "image/jpeg" as const,
            });
          }
        });
      } else if (typeof message.content === "string") {
        parts.push({ type: "text", text: message.content });
      }
    } else if (message.role === "tool") {
      let contentText: string;
      if (typeof message.content === "string") {
        contentText = message.content;
      } else if (Array.isArray(message.content)) {
        const textItem = message.content.find((c: any) => c.type === "text");
        contentText = textItem?.text || JSON.stringify(message.content);
      } else {
        contentText = JSON.stringify(message.content);
      }

      const toolCallId = (message as any).tool_call_id || `tool-${i}`;
      const toolName = (message as any).name || "add_cell";
      const parsedResult = parseToolResult(contentText, toolCallId, i);

      parts.push({
        type: "tool-invocation",
        toolInvocation: {
          toolName,
          toolCallId,
          state: "result",
          result: parsedResult,
        },
      });
    } else {
      if (assistantText) {
        parts.push({ type: "text", text: assistantText });
      }
      if (Array.isArray(message.tool_calls)) {
        message.tool_calls.forEach((toolCall) => {
          let args: Record<string, unknown> = {};
          try {
            const rawArgs = toolCall?.function?.arguments;
            if (typeof rawArgs === "string" && rawArgs.trim()) {
              args = JSON.parse(rawArgs);
            } else if (rawArgs && typeof rawArgs === "object") {
              args = rawArgs as Record<string, unknown>;
            }
          } catch {
            args = {};
          }

          parts.push({
            type: "tool-invocation",
            toolInvocation: {
              toolName: toolCall?.function?.name || "add_cell",
              toolCallId: toolCall?.id || `tool-call-${i}`,
              state: "call",
              args,
            },
          });
        });
      }
    }

    const transformedMessage: any = {
      id: `msg-${i}`,
      parts: parts as UIMessage["parts"],
      role: (message.role === "tool"
        ? "assistant"
        : message.role) as UIMessage["role"],
      content:
        message.role === "assistant"
          ? assistantText
          : message.role === "tool"
            ? ""
            : Array.isArray(message.content)
              ? message.content.find(
                  (c) =>
                    c.type === "text" &&
                    (c as { tag?: string }).tag !== "skill_instruction_metadata" &&
                    (c as { tag?: string }).tag !== "data_requested_metadata",
                )?.text || ""
              : message.content || "",
      experimental_attachments:
        attachments.length > 0 ? attachments : undefined,
      createdAt: message.created_at
        ? new Date(message.created_at + "Z")
        : undefined,
      ...(message.sub_agents ? { subAgents: true } : {}),
      ...((message.skill_ids && message.skill_ids.length) || message.skill_id
        ? { skillIds: message.skill_ids?.length ? message.skill_ids : [message.skill_id as string] }
        : {}),
    };

    const lastMessage = result[result.length - 1];
    if (
      transformedMessage.role === "assistant" &&
      lastMessage?.role === "assistant"
    ) {
      transformedMessage.parts.forEach((part: any) => {
        mergeAssistantPart(lastMessage, part);
      });
      if (
        typeof lastMessage.content === "string" &&
        typeof transformedMessage.content === "string"
      ) {
        if (transformedMessage.content) {
          lastMessage.content = lastMessage.content
            ? `${lastMessage.content}\n${transformedMessage.content}`
            : transformedMessage.content;
        }
      }
    } else {
      result.push(transformedMessage);
    }
  }

  return result;
}

// Fetch sessions with transformation
export const fetchSessions = async (
  userId: string,
  search?: string
) => {
  const url = `${API_BASE_URL}/sessions?user_id=${userId}${
    search ? `&search=${encodeURIComponent(search)}` : ""
  }`;
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to fetch sessions");
  const data = await response.json();

  return data.sessions.map(transformToChat);
};
